import React from 'react';
import { Link } from 'react-router-dom';
import { Play, StopCircle, Calculator, Ticket, FileText, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { apiDownload } from '@/lib/api';

/**
 * Voting control card: buttons to open/close/tabulate voting and download reports.
 * Extracted from AdminStageDetail.jsx to reduce complexity.
 */
export function StageActionsCard({ stage, id, onRequestAction }) {
  const canOpen = stage.status === 'scheduled' && stage.band_a_id && stage.band_b_id && stage.commands_total > 0;
  const canClose = stage.status === 'voting_open';
  const canTabulate = stage.status === 'voting_closed' || stage.status === 'voting_open';
  const canDelete = stage.status !== 'voting_open';
  const deleteLabel = stage.status === 'finished' ? 'Excluir etapa finalizada' : 'Excluir etapa';

  const openReportPdf = async () => {
    try {
      toast.info('Gerando relatório...');
      const r = await apiDownload.get(`/admin/stages/${id}/report/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch (e) {
      const msg = e.code === 'ECONNABORTED'
        ? 'Tempo esgotado ao gerar relatório. Tente novamente.'
        : (e.response?.data?.detail || 'Erro ao gerar relatório');
      toast.error(msg);
    }
  };

  return (
    <Card className="border border-border bg-card">
      <CardContent className="p-5">
        <div className="font-display text-2xl text-white mb-3">CONTROLE DA VOTAÇÃO</div>
        <div className="flex flex-wrap gap-3">
          <Button
            data-testid="stage-open-voting"
            disabled={!canOpen}
            onClick={() => onRequestAction('open')}
            className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]"
          >
            <Play className="w-4 h-4 mr-1" /> Abrir votação
          </Button>
          <Button
            data-testid="stage-close-voting"
            disabled={!canClose}
            onClick={() => onRequestAction('close')}
            variant="secondary"
          >
            <StopCircle className="w-4 h-4 mr-1" /> Fechar votação
          </Button>
          <Button
            data-testid="stage-tabulate"
            disabled={!canTabulate}
            onClick={() => onRequestAction('tabulate')}
            variant="secondary"
          >
            <Calculator className="w-4 h-4 mr-1" /> Apurar votos
          </Button>
          <Link to={`/admin/etapas/${id}/comandas`}>
            <Button data-testid="stage-commands-link" variant="secondary">
              <Ticket className="w-4 h-4 mr-1" /> Comandas
            </Button>
          </Link>
          <Button
            onClick={openReportPdf}
            variant="secondary"
            disabled={stage.status !== 'finished'}
            data-testid="stage-report-pdf"
          >
            <FileText className="w-4 h-4 mr-1" /> Relatório PDF
          </Button>
          <Button
            data-testid="stage-delete"
            disabled={!canDelete}
            onClick={() => onRequestAction('delete')}
            variant="secondary"
            className="text-[var(--g66-red-2)]"
          >
            <Trash2 className="w-4 h-4 mr-1" /> {deleteLabel}
          </Button>
        </div>
        {!canOpen && stage.status === 'scheduled' && (
          <div className="text-xs text-muted-foreground mt-3">
            {(!stage.band_a_id || !stage.band_b_id) ? 'Cadastre as duas bandas para abrir a votação. ' : ''}
            {stage.commands_total === 0 ? 'Gere comandas antes de abrir a votação.' : ''}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Silence unused import warning if any consumer forgets Loader2 elsewhere.
export const _StageActionsCardLoader = Loader2;
